import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConfluenceLogo } from '@atlaskit/logo';

import { BlueWaveTemplate } from '@/components/signup/layouts/blue-wave-template';
import { JoinableSites } from '@/components/signup/site-activation/components/common/joinable-sites';
import { ReturnToProduct } from '@/components/signup/site-activation/components/common/return-to-product';
import { SitePicker } from '@/components/signup/site-activation/components/common/site-picker';
import { ExistingUser } from '@/components/signup/site-activation/components/existing-user/existing-user';
import { UpgradeEligibleUser } from '@/components/signup/site-activation/components/upgrade-eligible-user/upgrade-eligible-user';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { getFirstName } from '@/components/signup/utils/get-first-name';
import {
	COHORTS,
	useCohort,
	useIsConfluenceContactCompanyExpandEligible,
} from '@/components/user-context/helpers/cohorts';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { Picture } from '@/components/wpl/image/src/image';

import { InputEmail } from '../../components/input-email';
import { NoCreditCardRequired } from '../../components/no-credit-card-required';
import { SubmitButtonExistingUser } from '../../components/submit-button-existing-user';
import { SubmitButtonMessageNext } from '../../components/submit-button-message-next';
import { useGetExistingSites } from '../../utils/get-existing-sites/use-get-existing-sites';

import { HubspotDockerTwilioGoPro } from './hubspot-docker-twilio-gopro';

export const LoggedInConfluenceBlueWaveTemplate = ({
	productEditionText,
}: {
	productEditionText: string;
}) => {
	const { formatMessage } = useIntl();
	const { user } = useUserContext();

	// Upgradeable Standard Sites Experiment
	const existingSites = useGetExistingSites();
	const siteActivationContent = existingSites ? (
		<UpgradeEligibleUser SubmitButton={<SubmitButtonMessageNext />} />
	) : undefined;

	const useContactExpandCohortLogic = useIsConfluenceContactCompanyExpandEligible();

	const cohort = useCohort(useContactExpandCohortLogic);

	if (!user.isUserLoggedIn || !cohort) {
		return null;
	}

	const showingReturnToProduct =
		cohort === COHORTS.CURRENT_PRODUCT_USER || cohort === COHORTS.CURRENT_PRODUCT_JOINABLE_USER;

	const username = getFirstName(user.name);

	return (
		<BlueWaveTemplate
			productEditionText={productEditionText}
			socialProofLogos={<HubspotDockerTwilioGoPro />}
			tagline={formatMessage({
				id: 'WacSignupForm.signupOfferingIncludesFeaturesSocialProofTextConfluence',
				defaultMessage: 'Trusted by over 75,000 teams worldwide',
			})}
			valueProps={formatMessage(
				{
					id: 'WacSignupForm.signupOfferingIncludesFeaturesFreeConfluence',
					defaultMessage:
						'Kickstart team collaboration{newline}Increase transparency{newline}Capture all the knowledge{newline}Build admirable content',
				},
				{ newline: '{newline}' },
			).split('{newline}')}
			productLogos={[<ConfluenceLogo appearance="inverse" size="xlarge" key="jsm" />]}
			illustration={
				<Picture
					image={{
						src: 'https://atlassian.com/dam/jcr:12b69d08-6ae8-4b14-ac1c-f3092ccaff97/Confluence%20Hero.svg',
						alt: '',
					}}
				/>
			}
			showAtlassianLogo={!showingReturnToProduct}
		>
			<div css={containerCss}>
				<h2 css={siteActivationHeadingCss}>
					<FormattedMessage
						id="WacSignupForm.signupFormPersonalizedGreeting"
						defaultMessage="Welcome back, {username}"
						values={{ username }}
					/>
				</h2>
				{!showingReturnToProduct && <InputEmail email={user.email} />}

				<SiteActivation
					existingSites={existingSites}
					useContactExpandCohortLogic={useContactExpandCohortLogic}
					UpgradeEligibleUser={siteActivationContent}
					CurrentProductJoinableUser={<JoinableSites />}
					CurrentProductUser={<ReturnToProduct />}
					WeekOneUser={siteActivationContent ?? null}
					ExistingUser={
						<ExistingUser
							SitePicker={
								<div css={sitePickerCss}>
									<SitePicker />
								</div>
							}
							SubmitButton={<SubmitButtonExistingUser />}
						/>
					}
				/>
				{!showingReturnToProduct && <NoCreditCardRequired />}
			</div>
		</BlueWaveTemplate>
	);
};

const containerCss = css({
	display: 'grid',
	gap: '8px',
	marginBlockEnd: 48,
});

const siteActivationHeadingCss = css({
	fontSize: 24,
	fontWeight: 500,
	margin: 0,
	textAlign: 'center',
});

const sitePickerCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['label + div > div:first-of-type']: {
		borderRadius: '3px !important',
		// eslint-disable-next-line local/ensure-wac-token-usage
		border: '2px solid rgb(179, 186, 197) !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input[disabled]']: {
		paddingInlineStart: '8px !important',
	},
});
